import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const INIT = {
  order_id: '', customer_id: '', amount: '', status: 'draft',
  due_date: '', paid_date: '', notes: ''
};
const STATUSES = ['draft', 'sent', 'paid', 'overdue', 'cancelled'];
const LIMIT = 15;

function fmtMoney(n) { return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function Invoices({ setTitle }) {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [status, setStatus]     = useState('');
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [form, setForm]         = useState(INIT);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [customers, setCustomers] = useState([]);

  useEffect(() => { setTitle('Invoices'); }, [setTitle]);
  useEffect(() => {
    api.get('/customers/all').then(r => setCustomers(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/invoices?status=${status}&page=${page}&limit=${LIMIT}`);
      setItems(data.data || []); setTotal(data.total || 0);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [status, page]);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => {
    setForm({ ...INIT, due_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10) });
    setEditId(null); setModal(true);
  };
  const openEdit = (item) => {
    setForm({
      order_id: item.order_id || '',
      customer_id: item.customer_id || '',
      amount: item.amount ?? '',
      status: item.status,
      due_date: item.due_date ? item.due_date.slice(0, 10) : '',
      paid_date: item.paid_date ? item.paid_date.slice(0, 10) : '',
      notes: item.notes || '',
    });
    setEditId(item.id); setModal(true);
  };
  const openView = async (id) => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setViewModal(data);
    } catch { alert('Could not load invoice'); }
  };
  const closeModal = () => setModal(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.customer_id) return alert('Customer is required');
    if (!form.amount) return alert('Amount is required');
    setSaving(true);
    try {
      if (editId) await api.put(`/invoices/${editId}`, form);
      else        await api.post('/invoices', form);
      closeModal(); load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving invoice');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, inv) => {
    if (!window.confirm(`Delete invoice ${inv}?`)) return;
    try { await api.delete(`/invoices/${id}`); load(); }
    catch (err) { alert(err.response?.data?.error || 'Error deleting'); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Invoices</h1>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Invoice
        </button>
      </div>

      <div className="card">
        <div className="toolbar">
          <div className="tab-bar" style={{ padding: 0, border: 0, gap: 6 }}>
            <button className={`tab-btn ${status === '' ? 'active' : ''}`} onClick={() => { setStatus(''); setPage(1); }}>All</button>
            {STATUSES.map(s => (
              <button key={s} className={`tab-btn ${status === s ? 'active' : ''}`} onClick={() => { setStatus(s); setPage(1); }}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{total} invoices</span>
        </div>

        <div className="table-wrap">
          {loading ? <div className="loading">Loading…</div> : (
            <table>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Due Date</th>
                  <th>Paid Date</th>
                  <th className="text-right">Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={7} className="empty">No invoices found</td></tr>
                ) : items.map(item => (
                  <tr key={item.id}>
                    <td className="font-mono"><strong>{item.invoice_number}</strong></td>
                    <td>{item.customer_name || '—'}</td>
                    <td><span className={`badge badge-${item.status}`}>{item.status}</span></td>
                    <td>{item.due_date ? item.due_date.slice(0, 10) : '—'}</td>
                    <td>{item.paid_date ? item.paid_date.slice(0, 10) : '—'}</td>
                    <td className="text-right"><strong>{fmtMoney(item.amount)}</strong></td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon" title="View" onClick={() => openView(item.id)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z"/><circle cx="12" cy="12" r="3"/></svg>
                        </button>
                        <button className="btn-icon" title="Edit" onClick={() => openEdit(item)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(item.id, item.invoice_number)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <span>Showing {Math.min((page - 1) * LIMIT + 1, total)}–{Math.min(page * LIMIT, total)} of {total}</span>
            <div className="pagination-btns">
              <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editId ? 'Edit Invoice' : 'New Invoice'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full">
                  <label>Customer *</label>
                  <select className="form-control" value={form.customer_id} onChange={e => f('customer_id', e.target.value)}>
                    <option value="">Select customer…</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount *</label>
                  <input type="number" className="form-control" value={form.amount} onChange={e => f('amount', e.target.value)} placeholder="0.00" step="0.01" min="0" />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-control" value={form.status} onChange={e => f('status', e.target.value)}>
                    {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Due Date</label>
                  <input type="date" className="form-control" value={form.due_date} onChange={e => f('due_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Paid Date</label>
                  <input type="date" className="form-control" value={form.paid_date} onChange={e => f('paid_date', e.target.value)} />
                </div>
                <div className="form-group full">
                  <label>Notes</label>
                  <textarea className="form-control" value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Optional notes…" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Update Invoice' : 'Create Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      {viewModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setViewModal(null)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <h2>Invoice {viewModal.invoice_number}</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost btn-sm no-print" onClick={() => window.print()}>Print</button>
                <button className="modal-close" onClick={() => setViewModal(null)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Invoice #</label>
                  <div className="font-mono">{viewModal.invoice_number}</div>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <div><span className={`badge badge-${viewModal.status}`}>{viewModal.status}</span></div>
                </div>
                <div className="form-group">
                  <label>Customer</label>
                  <div>{viewModal.customer_name || '—'}</div>
                </div>
                <div className="form-group">
                  <label>Amount</label>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{fmtMoney(viewModal.amount)}</div>
                </div>
                <div className="form-group">
                  <label>Due Date</label>
                  <div>{viewModal.due_date ? viewModal.due_date.slice(0, 10) : '—'}</div>
                </div>
                <div className="form-group">
                  <label>Paid Date</label>
                  <div>{viewModal.paid_date ? viewModal.paid_date.slice(0, 10) : '—'}</div>
                </div>
                {viewModal.notes && (
                  <div className="form-group full">
                    <label>Notes</label>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{viewModal.notes}</div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setViewModal(null)}>Close</button>
              <button className="btn btn-primary" onClick={() => { setViewModal(null); openEdit(viewModal); }}>Edit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
